import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },

  multFieldLine: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
}));

const ChatbotsSchema = Yup.object().shape({
  queries: Yup.string()
    .min(1, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),

  replyMessage: Yup.string()
    .required("Required"),

  requeries: Yup.string()
    .required("Required"),

  isAgent: Yup.boolean(),

  isImg: Yup.boolean(),

  imgName: Yup.string(),
});

const BotModal = ({ open, onClose, botId, id }) => {
  const classes = useStyles();

  const initialState = {
    queries: "",
    replyMessage: "",
    requeries: "",
    isAgent: false,
    isImg: false,
    imgName: "",
  };

  const [bots, setBots] = useState(initialState);
  const [isImgSelected, setIsImgSelected] = useState(false);

  // ============================================================
  // CARGAR QUERY PARA EDITAR
  // ============================================================

  useEffect(() => {
    const loadBotQuery = async () => {
      if (!botId) {
        setBots(initialState);
        setIsImgSelected(false);
        return;
      }

      try {
        const { data } = await api.get(`/botsquerry/${botId}`);

        setBots({
          queries: data.queries || "",
          replyMessage: data.replyMessage || "",
          requeries: data.requeries || "",
          isAgent: Boolean(data.isAgent),
          isImg: Boolean(data.isImg),
          imgName: data.imgName || "",
        });

        setIsImgSelected(Boolean(data.isImg));
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      loadBotQuery();
    }
  }, [botId, open]);

  // ============================================================
  // CERRAR
  // ============================================================

  const handleClose = () => {
    setBots(initialState);
    setIsImgSelected(false);
    onClose();
  };

  // ============================================================
  // GUARDAR
  // ============================================================

  const handleSaveBots = async (values) => {
    const updatedValues = {
      queries: values.queries,
      replyMessage: values.replyMessage,
      requeries: values.requeries,
      isAgent: Boolean(values.isAgent),
      isImg: Boolean(values.isImg),
      imgName: values.imgName || "",
      botsId: Number(id),
    };

    try {
      if (botId) {
        await api.put(
          `/botsquerry/${botId}`,
          updatedValues
        );
      } else {
        await api.post(
          "/botsquerry",
          updatedValues
        );
      }

      toast.success(i18n.t("botModal.success"));

      handleClose();
    } catch (err) {
      console.error(
        "[BotQueriesModal] Error guardando query:",
        err
      );

      toastError(err);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {botId
            ? i18n.t("botQueriesModal.title.edit")
            : i18n.t("botQueriesModal.title.add")}
        </DialogTitle>

        <Formik
          initialValues={bots}
          enableReinitialize
          validationSchema={ChatbotsSchema}
          onSubmit={handleSaveBots}
        >
          {({
            touched,
            errors,
            isSubmitting,
            values,
            setFieldValue,
          }) => (
            <Form>
              <DialogContent dividers>

                {/* =====================================================
                    BOT ID
                ====================================================== */}

                <TextField
                  label="Bot ID"
                  value={id || ""}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                />

                {/* =====================================================
                    QUERY
                ====================================================== */}

                <Field
                  as={TextField}
                  label={i18n.t(
                    "botQueriesModal.form.queries"
                  )}
                  multiline
                  rows={5}
                  fullWidth
                  name="queries"
                  error={
                    touched.queries &&
                    Boolean(errors.queries)
                  }
                  helperText={
                    touched.queries &&
                    errors.queries
                  }
                  variant="outlined"
                  margin="dense"
                />

                {/* =====================================================
                    RESPUESTA
                ====================================================== */}

                <Field
                  as={TextField}
                  label={i18n.t(
                    "botQueriesModal.form.replies"
                  )}
                  multiline
                  rows={5}
                  fullWidth
                  name="replyMessage"
                  error={
                    touched.replyMessage &&
                    Boolean(errors.replyMessage)
                  }
                  helperText={
                    touched.replyMessage &&
                    errors.replyMessage
                  }
                  variant="outlined"
                  margin="dense"
                />

                {/* =====================================================
                    CONFIGURACIONES
                ====================================================== */}

                <div className={classes.multFieldLine}>

                  {/* REQUIERE */}

                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.formControl}
                  >
                    <InputLabel>
                      {i18n.t(
                        "botQueriesModal.form.requierir"
                      )}
                    </InputLabel>

                    <Field
                      as={Select}
                      name="requeries"
                      label={i18n.t(
                        "botQueriesModal.form.requierir"
                      )}
                    >
                      <MenuItem value="">
                        &nbsp;
                      </MenuItem>

                      <MenuItem value="cedula">
                        Cédula
                      </MenuItem>

                      <MenuItem value="none">
                        Ninguno
                      </MenuItem>
                    </Field>
                  </FormControl>

                  {/* ES AGENTE */}

                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.formControl}
                  >
                    <InputLabel>
                      {i18n.t(
                        "botQueriesModal.form.isAgent"
                      )}
                    </InputLabel>

                    <Select
                      value={
                        values.isAgent ? "1" : "0"
                      }
                      label={i18n.t(
                        "botQueriesModal.form.isAgent"
                      )}
                      onChange={(event) => {
                        setFieldValue(
                          "isAgent",
                          event.target.value === "1"
                        );
                      }}
                    >
                      <MenuItem value="0">
                        No
                      </MenuItem>

                      <MenuItem value="1">
                        Sí
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* IMAGEN */}

                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.formControl}
                  >
                    <InputLabel>
                      {i18n.t(
                        "botQueriesModal.form.isImg"
                      )}
                    </InputLabel>

                    <Select
                      value={
                        values.isImg ? "1" : "0"
                      }
                      label={i18n.t(
                        "botQueriesModal.form.isImg"
                      )}
                      onChange={(event) => {
                        const enabled =
                          event.target.value === "1";

                        setFieldValue(
                          "isImg",
                          enabled
                        );

                        setIsImgSelected(enabled);
                      }}
                    >
                      <MenuItem value="0">
                        No
                      </MenuItem>

                      <MenuItem value="1">
                        Sí
                      </MenuItem>
                    </Select>
                  </FormControl>
                </div>

                {/* =====================================================
                    NOMBRE IMAGEN
                ====================================================== */}

                {isImgSelected && (
                  <Field
                    as={TextField}
                    label={i18n.t(
                      "botQueriesModal.form.imgName"
                    )}
                    name="imgName"
                    error={
                      touched.imgName &&
                      Boolean(errors.imgName)
                    }
                    helperText={
                      touched.imgName &&
                      errors.imgName
                    }
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                )}

              </DialogContent>

              {/* =======================================================
                  BOTONES
              ======================================================== */}

              <DialogActions>

                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t(
                    "botModal.buttons.cancel"
                  )}
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {botId
                    ? i18n.t(
                        "botModal.buttons.okEdit"
                      )
                    : i18n.t(
                        "botModal.buttons.okAdd"
                      )}

                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={
                        classes.buttonProgress
                      }
                    />
                  )}
                </Button>

              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default BotModal;